/** @format */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../utils/analytics";
import { useWhatsChanged } from "./useWhatsChanged";
import { WhatsChangedPanel } from "./WhatsChangedPanel";

const PANEL_ID = "whats-changed-panel";

const EXIT_ANIMATION_MS = 200;

export const WhatsChangedButton = () => {
	const { t } = useTranslation();
	const { releases, currentVersion, unseenCount, markSeen } = useWhatsChanged();
	const [isOpen, setIsOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const pillRef = useRef<HTMLButtonElement>(null);
	const closeTimeoutRef = useRef<number | null>(null);

	const hasUnseen = unseenCount > 0;

	useEffect(
		() => () => {
			if (closeTimeoutRef.current !== null) {
				window.clearTimeout(closeTimeoutRef.current);
			}
		},
		[],
	);
	const openPanel = () => {
		if (closeTimeoutRef.current !== null) {
			window.clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setIsClosing(false);
		if (!isOpen) {
			trackEvent("whats_changed_open", { location: "floating_button" });
			markSeen();
			setIsOpen(true);
		}
	};

	const closePanel = () => {
		if (!isOpen || isClosing) {
			return;
		}
		setIsClosing(true);
		pillRef.current?.focus();
		closeTimeoutRef.current = window.setTimeout(() => {
			closeTimeoutRef.current = null;
			setIsClosing(false);
			setIsOpen(false);
		}, EXIT_ANIMATION_MS);
	};

	const togglePanel = () => {
		if (isOpen && !isClosing) {
			closePanel();
		} else {
			openPanel();
		}
	};

	return (
		<>
			{isOpen && !isClosing && (
				<div
					className="fixed inset-0 z-40"
					onClick={closePanel}
					aria-hidden="true"
					data-testid="whats-changed-overlay"
				/>
			)}
			<div className="fixed bottom-24 left-5 z-50 flex flex-col items-start gap-3">
				{isOpen && (
					<WhatsChangedPanel
						id={PANEL_ID}
						releases={releases}
						currentVersion={currentVersion}
						isClosing={isClosing}
						onClose={closePanel}
					/>
				)}
				<button
					ref={pillRef}
					type="button"
					onClick={togglePanel}
					aria-expanded={isOpen && !isClosing}
					aria-controls={PANEL_ID}
					aria-label={
						hasUnseen ? t("whatsChanged.ariaLabelNew", { count: unseenCount }) : t("whatsChanged.ariaLabelNone")
					}
					className="flex cursor-pointer items-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-semibold text-(--public-color-text-default) shadow-lg transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--public-color-theme-primary)"
				>
					<span
						className={`inline-flex h-3 w-3 rounded-full ${hasUnseen ? "bg-green-600" : "bg-amber-600"}`}
						aria-hidden="true"
					/>
					<span className="hidden sm:inline">{t("whatsChanged.label")}</span>
				</button>
			</div>
		</>
	);
};
